import { CommonModule } from '@angular/common';
import { Component, inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';

import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';

import { ChangeDeviceService } from '@app/shared/services/change-device.service';
import { ChangeStepService } from '@app/core/components/stepper/service/change-step.service';
import { SaveDataService } from '@app/shared/services/save-data.service';

import { InputSwitchChangeEvent } from 'primeng/inputswitch';
import { CoreModule } from '@app/core/core.module';
import { AppService } from '@app/app.service';
import { ExpansionOverviewExample } from "./expansion-settings/expansion-settings";
import { ToogleSwitchComponent } from '@app/core/components/toogle-switch/toogle-switch.component';


@Component({
    selector: 'app-privacy-settings',
    standalone: true,
    imports: [CoreModule, CommonModule, TranslateModule, SharedModule, ExpansionOverviewExample],
    templateUrl: './privacy-settings.component.html',
    styleUrl: './privacy-settings.component.scss',
})

export class PrivacySettingsComponent {

    @ViewChildren(ToogleSwitchComponent) togglels!: QueryList<ToogleSwitchComponent>;

    private saveDataService = inject(SaveDataService);
    private appData = inject(AppService);

    continueButton = 'common.buttons.continueButton';
    backButton = 'common.buttons.backButton';

    privacySettingsTitle = "features.privacySettings.privacySettingsTitle";
    privacySettingsSubtitle = "features.privacySettings.privacySettingsSubtitle";
    textCheckbox1 = "features.privacySettings.textCheckbox1";
    textCheckbox2 = "features.privacySettings.textCheckbox2";
    textCheckbox3 = "features.privacySettings.textCheckbox3";
    textCheckbox4 = "features.privacySettings.textCheckbox4";
    furtherInformation = "features.privacySettings.furtherInformation";
    lessInformation = "features.privacySettings.lessInformation";

    backButtonDisabled = false;
    continueButtonDisabled = true;

    isPortraitDevice = false;

    formGroup!: FormGroup;

    selectedPrivacy1: 'y' | 'n' | '' = '';
    selectedPrivacy2: 'y' | 'n' | '' = '';
    selectedPrivacy3: 'y' | 'n' | '' = '';
    selectedPrivacy4: 'y' | 'n' | '' = '';

    private updatingFromPrivacy1 = false;


    constructor(
      private route: ActivatedRoute,
      private router: Router,
      private changeDeviceService: ChangeDeviceService,
      //private saveDataService: SaveDataService,
      private changeStepService: ChangeStepService,
      private fb: FormBuilder
    ) {
        this.changeStepService.changeStep(1);
    }

    ngOnInit() {
      this.isPortraitDevice = this.changeDeviceService.isPortraitDevice;
      this.changeDeviceService.changedDevice.subscribe(value => {this.isPortraitDevice = value;});

      this.createForm();
      //this.setValuesForm(this.appData.data.userData);

      this.formIsValid();
    }

    ngAfterViewInit() {
      this.setValuesForm(this.appData.data.userData);
    }

    createForm() {
        this.formGroup = this.fb.group({
            switchPrivacy1: new FormControl(this.selectedPrivacy1),
            switchPrivacy2: new FormControl(this.selectedPrivacy2, [Validators.required, Validators.minLength(1)]),
            switchPrivacy3: new FormControl(this.selectedPrivacy3, [Validators.required, Validators.minLength(1)]),
            switchPrivacy4: new FormControl(this.selectedPrivacy4, [Validators.required, Validators.minLength(1)])
        });
    }

    setValuesForm(value?:any) {
        if(value != undefined) {
            this.formGroup = this.fb.group({
              switchPrivacy1: new FormControl(value.privacySettings.switchPrivacy1 === true ?
                this.togglels.get(0)?.onclickYes() : value.privacySettings.switchPrivacy1 === false ?
                this.togglels.get(0)?.onclickNo() : this.togglels.get(0)?.onSetNeutral()),
              switchPrivacy2: new FormControl(value.privacySettings.switchPrivacy2 === true ?
                this.togglels.get(1)?.onclickYes() : value.privacySettings.switchPrivacy2 === false ?
                this.togglels.get(1)?.onclickNo() : this.togglels.get(1)?.onSetNeutral(), [Validators.required, Validators.minLength(1)]),
              switchPrivacy3: new FormControl(value.privacySettings.switchPrivacy3 === true ?
                this.togglels.get(2)?.onclickYes() : value.privacySettings.switchPrivacy3 === false ?
                this.togglels.get(2)?.onclickNo() : this.togglels.get(2)?.onSetNeutral(), [Validators.required, Validators.minLength(1)]),
              switchPrivacy4: new FormControl(value.privacySettings.switchPrivacy4 === true ?
                this.togglels.get(3)?.onclickYes() : value.privacySettings.switchPrivacy4 === false ?
                this.togglels.get(3)?.onclickNo() : this.togglels.get(3)?.onSetNeutral(), [Validators.required, Validators.minLength(1)]),
              });
        }
    }

    //-------------------------//
    //------EVENT METHODS------//
    //-------------------------//

    onChangePrivacy1(state: boolean | null) {

        this.formGroup.controls.switchPrivacy1.setValue(state);
        if(state != null) {
            this.updatingFromPrivacy1 = true;
            this.formGroup.controls.switchPrivacy2.setValue(state);
            this.formGroup.controls.switchPrivacy3.setValue(state);
            this.formGroup.controls.switchPrivacy4.setValue(state);
            this.togglels.forEach(toggle => state == true ? toggle.switchState = 'y' : toggle.switchState = 'n');
            this.updatingFromPrivacy1 = false;
        }

        this.formIsValid();
    }


    onChangePrivacy2(state: boolean | null) {
        if (!this.updatingFromPrivacy1) {
          this.formGroup.controls.switchPrivacy2.setValue(state);
          this.needResetToggle1();
          this.formIsValid();
        }
    }

    onChangePrivacy3(state: boolean | null) {
        if (!this.updatingFromPrivacy1) {
            this.formGroup.controls.switchPrivacy3.setValue(state);
            this.needResetToggle1();
            this.formIsValid();
        }
    }

    onChangePrivacy4(state: boolean | null) {
        if (!this.updatingFromPrivacy1) {
            this.formGroup.controls.switchPrivacy4.setValue(state);
            this.needResetToggle1();
            this.formIsValid();
        }
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    formIsValid() {
        this.continueButtonDisabled = !this.formGroup.valid;
    }

    needResetToggle1() {
        if (this.formGroup.controls.switchPrivacy2.value == true
            && this.formGroup.controls.switchPrivacy3.value == true
            && this.formGroup.controls.switchPrivacy4.value == true) {
              this.togglels.get(0)?.onclickYes();
        } else if (this.formGroup.controls.switchPrivacy2.value == false
            && this.formGroup.controls.switchPrivacy3.value == false
            && this.formGroup.controls.switchPrivacy4.value == false) {
              this.togglels.get(0)?.onclickNo();
        } else {
          this.togglels.get(0)?.onSetNeutral();
        }
    }

    //-------------------------//
    //--------NAVIGATION-------//
    //-------------------------//

    onClicButtonBack() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'privacy-settings');
        this.router.navigate(['/main/financing-start'], { relativeTo: this.route });
    }

    onClicButtonContinue() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'privacy-settings');
        this.router.navigate(['/main/step/identification'], { relativeTo: this.route });
    }

    ngOnDestroy() {
        this.togglels.forEach(toggle => toggle.onSetNeutral());
    }
}
